import { createUseStyles } from "react-jss";
import {
    inputBackgroundColor,
    controlBackgroundColor,
    alpha,
    brighten,
    darken,
    borderColor,
    foregroundColor,
    backgroundless,
} from "../../theme/Mixins";
import * as ChoiceTokens from "../../theme/choiceThemeTokens";
import * as InputTokens from "../../theme/inputThemeTokens";
import { Theme } from "../../types/Theme";

type SelectStyleProps = {
    theme: Theme;
    autoComplete?: boolean;
    icon?: unknown;
    size?: string;
    disabled?: boolean;
    selected?: boolean;
};

const SELECT_OPTION_SELECTED_FONT_WEIGHT = 600;

/**
 * Combobox padding: horizontal clearance for accessories only.
 * Vertical padding stays 0 so label/caret stay centered via container line-height
 * (unlike InputBox, where padding lives on the inner input).
 */
function modernSelectContentPadding({
    icon,
    disabled,
}: {
    icon?: unknown;
    disabled?: boolean;
}) {
    const padLeft = InputTokens.resolveSelectComboboxContentPaddingLeft(Boolean(icon));
    const padRight = InputTokens.resolveSelectComboboxContentPaddingRight(disabled !== true);
    return [[0, padRight, 0, padLeft]];
}

function legacySelectHoverSurface(theme: Theme) {
    if (backgroundless(theme)) {
        return {};
    }
    return {
        backgroundColor: alpha(
            darken(theme.ControlBackground, theme.ActiveBackgroundValue),
            theme.ControlAlpha,
        ),
    };
}

/**
 * Returns `undefined`, not `""`, for "no underline" — the opposite of
 * {@link InputTokens.resolveTextInputBorderBottom}, deliberately, because these rules also set the
 * `border` shorthand and `undefined` is a no-op where `""` actively removes the longhand:
 *   * legacy: `border: 1px solid` followed by `border-bottom: ""` leaves only top/right/left, so a
 *     bordered combobox would lose its bottom edge (verified against the CSSOM).
 *   * modern: returning `""` here regresses the `selectExpanded` / `selectFocused` cascade — the
 *     PrimaryColor inset outline stops winning over the resting one (two Select specs fail).
 * `InputBox` can return `""` safely because it sets `border-bottom` as a real value, never as a
 * removal alongside a `border` shorthand in the same rule.
 */
function resolveSelectBorderBottom(theme: Theme, focused?: boolean) {
    if (InputTokens.useModernInputChrome(theme)) {
        // Modern draws the underline as an inset box-shadow instead — see modernSelectOutline. The
        // `border: 0` shorthand in these same rules would otherwise erase a border-bottom longhand.
        return undefined;
    }
    if (InputTokens.usesUnderlineOnlyField(theme)) {
        return [
            [
                InputTokens.FIELD_UNDERLINE_WIDTH_PX,
                "solid",
                legacySelectBorderColor(theme, focused),
            ],
        ];
    }
    return undefined;
}

function legacySelectBorderColor(theme: Theme, focused?: boolean) {
    if (focused) {
        return borderColor(theme, theme.PrimaryColor);
    }
    return theme.ControlBorderColor;
}

/** Modern combobox edge — default border matches text answer inputs; other states unchanged. */
function modernSelectOutline(
    theme: Theme,
    state?: {
        focused?: boolean;
        expanded?: boolean;
        hover?: boolean;
        selected?: boolean;
    },
) {
    if (!InputTokens.useModernInputChrome(theme)) {
        return undefined;
    }
    // Transparent + Borders = None: a flat light-grey bottom rule in every state, matching a text
    // input. Drawn here rather than as border-bottom so the `border: 0` shorthand cannot erase it.
    if (InputTokens.usesUnderlineOnlyField(theme)) {
        return InputTokens.resolveFieldUnderlineShadow(theme);
    }
    // A combobox is a field, so its edge follows Field Settings → Borders in both themes — same as
    // the legacy `border` below and the backgroundless underline guards, which already read it.
    const fieldWidth = InputTokens.resolveFieldBorderWidth(theme);
    const activeWidth = ChoiceTokens.resolveEmphasisBorderWidth(fieldWidth);
    if (state?.expanded) {
        return ChoiceTokens.resolveModernChoiceInsetBorder(theme, theme.PrimaryColor, activeWidth);
    }
    if (state?.focused) {
        if (state?.selected) {
            return ChoiceTokens.resolveModernChoiceInsetBorder(
                theme,
                theme.PrimaryColor,
                activeWidth,
            );
        }
        return ChoiceTokens.resolveModernChoiceInsetBorder(
            theme,
            InputTokens.resolveTextInputBorderColor(theme, false),
            fieldWidth,
        );
    }
    if (state?.hover) {
        return ChoiceTokens.resolveModernChoiceInsetBorder(
            theme,
            ChoiceTokens.resolveChoiceBorderColorHover(theme),
            fieldWidth,
        );
    }
    return ChoiceTokens.resolveModernChoiceInsetBorder(
        theme,
        InputTokens.resolveTextInputBorderColor(theme, false),
        fieldWidth,
    );
}

export default createUseStyles(
    (theme: Theme) =>
        ({
            select: {
                backgroundColor: ({ autoComplete }: SelectStyleProps) => {
                    // Non-autocomplete combobox keeps using ControlBackground, same as legacy,
                    // in both themes — only the autocomplete (text-input-like) variant uses
                    // InputBackground, also matching legacy's own split.
                    if (autoComplete) {
                        return InputTokens.useModernInputChrome(theme)
                            ? InputTokens.resolveTextInputBackground(theme)
                            : inputBackgroundColor(theme);
                    }
                    return alpha(theme.ControlBackground, theme.ControlAlpha);
                },
                border: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? 0
                        : [[theme.BorderWidth, "solid", legacySelectBorderColor(theme)]],
                borderBottom: () => resolveSelectBorderBottom(theme),
                // A combobox is a field, so it takes the field corner radius in both themes.
                borderRadius: () => InputTokens.resolveTextInputInnerBorderRadius(theme),
                boxShadow: () => modernSelectOutline(theme),
                boxSizing: "border-box",
                color: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? ChoiceTokens.resolveModernChoiceLabelColor(theme)
                        : theme.ForegroundColorDark,
                composes: "select",
                display: "inline-block",
                fontSize: ({ size }: { size?: string }) => size === "XSmall" && 13,
                // Via the token, matching InputBox.getHeight — a missing BorderWidth would otherwise
                // make this NaN here while the text input fell back to 1px, diverging the heights.
                height: theme.InputHeight + InputTokens.resolveFieldBorderWidth(theme) * 2,
                lineHeight: `${theme.InputHeight}px`,
                outline: "2px solid transparent",
                overflow: "visible",
                position: "relative",
                transition: "background 200ms, box-shadow 200ms, border-color 200ms, outline-color 200ms, color 200ms",
                verticalAlign: "middle",
                width: "100%",
            },
            /** Keyboard-only focus ring on the combobox shell (toggled from {@link Select}). */
            selectKeyboardFocus: {
                outline: ({ selected }: SelectStyleProps) =>
                    InputTokens.useModernInputChrome(theme)
                        ? ChoiceTokens.resolveModernChoiceFocusOutline(theme, selected)
                        : undefined,
                outlineOffset: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? ChoiceTokens.CHOICE_FOCUS_OUTLINE_OFFSET_PX
                        : undefined,
            },
            selectHover: {
                backgroundColor: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? ChoiceTokens.resolveChoiceBackgroundHover(theme)
                        : legacySelectHoverSurface(theme).backgroundColor,
                boxShadow: () => modernSelectOutline(theme, { hover: true }),
            },
            selectExpanded: {
                backgroundColor: ({ autoComplete }: SelectStyleProps) => {
                    if (!InputTokens.useModernInputChrome(theme)) {
                        return undefined;
                    }
                    // Autocomplete stays on the text-input path; plain dropdown matches
                    // the closed combobox (`ControlBackground` + `ControlAlpha`).
                    return autoComplete
                        ? InputTokens.resolveTextInputBackground(theme, true)
                        : alpha(theme.ControlBackground, theme.ControlAlpha);
                },
                color: ({ autoComplete }: SelectStyleProps) => {
                    if (!InputTokens.useModernInputChrome(theme)) {
                        return undefined;
                    }
                    return autoComplete
                        ? InputTokens.resolveTextInputForeground(theme, true)
                        : ChoiceTokens.resolveModernChoiceLabelColor(theme);
                },
                border: () => (InputTokens.useModernInputChrome(theme) ? 0 : undefined),
                borderBottom: () => resolveSelectBorderBottom(theme, true),
                boxShadow: () => modernSelectOutline(theme, { expanded: true }),
                borderColor: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? undefined
                        : legacySelectBorderColor(theme, true),
                borderRadius: () => {
                    if (InputTokens.useModernInputChrome(theme)) {
                        return undefined;
                    }
                    const radius = InputTokens.resolveTextInputInnerBorderRadius(theme);
                    return [[radius, radius, 0, 0]];
                },
                composes: "select--expanded",
                overflow: "visible",
            },
            selectFocused: {
                backgroundColor: ({ autoComplete }: { autoComplete?: boolean }) => {
                    if (InputTokens.useModernInputChrome(theme)) {
                        return autoComplete
                            ? InputTokens.resolveTextInputBackground(theme, true)
                            : alpha(theme.ControlBackground, theme.ControlAlpha);
                    }
                    return autoComplete
                        ? inputBackgroundColor(theme, true)
                        : alpha(
                            darken(theme.ControlBackground, theme.ActiveHighlightValue),
                            theme.ControlAlpha,
                        );
                },
                border: () => (InputTokens.useModernInputChrome(theme) ? 0 : undefined),
                borderBottom: () => resolveSelectBorderBottom(theme, true),
                boxShadow: ({ selected }: SelectStyleProps) =>
                    modernSelectOutline(theme, { focused: true, selected }),
                borderColor: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? undefined
                        : legacySelectBorderColor(theme, true),
                color: ({ autoComplete }: SelectStyleProps) => {
                    if (!InputTokens.useModernInputChrome(theme)) {
                        return undefined;
                    }
                    return autoComplete
                        ? InputTokens.resolveTextInputForeground(theme, true)
                        : ChoiceTokens.resolveModernChoiceLabelColor(theme);
                },
                composes: "select--focused",
                outlineColor: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? undefined
                        : backgroundless(theme) && theme.PrimaryColor,
            },
            selectDisabled: {
                composes: "select__disabled",
                opacity: 0.5,
                pointerEvents: "none",
            },
            selectDisabledBorderOnly: {
                composes: "select__disabled",
                border: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? 0
                        : [[theme.BorderWidth, "solid", InputTokens.resolveSelectDisabledDisplayBorderColor(theme)]],
                // Muted on purpose — keeps resolveSelectDisabledDisplayBorderColor rather than the
                // full-strength ControlBorderColor a text input uses. Drawn as an inset shadow for
                // the same reason as modernSelectOutline: `border: 0` above would erase a longhand.
                boxShadow: () => {
                    if (!InputTokens.useModernInputChrome(theme)) {
                        return undefined;
                    }
                    const mutedColor = InputTokens.resolveSelectDisabledDisplayBorderColor(theme);
                    if (InputTokens.usesUnderlineOnlyField(theme)) {
                        return InputTokens.resolveFieldUnderlineShadow(theme, mutedColor);
                    }
                    return ChoiceTokens.resolveModernChoiceInsetBorder(
                        theme,
                        mutedColor,
                        InputTokens.resolveFieldBorderWidth(theme),
                    );
                },
                pointerEvents: "none",
            },
            selectContent: {
                background: "transparent",
                border: 0,
                // Legacy sets no radius on the inner content, so this stays modern-only.
                borderRadius: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? InputTokens.resolveTextInputInnerBorderRadius(theme)
                        : undefined,
                boxSizing: "border-box",
                color: "inherit",
                composes: "select__content",
                display: "block",
                fontFamily: "inherit",
                fontSize: "inherit",
                height: "100%",
                lineHeight: "inherit",
                outline: "none",
                overflow: "hidden",
                padding: ({
                    icon,
                    size,
                    disabled,
                }: {
                    icon?: unknown;
                    size?: string;
                    disabled?: boolean;
                }) =>
                    InputTokens.useModernInputChrome(theme)
                        ? modernSelectContentPadding({ icon, disabled })
                        : size === "XSmall"
                            ? [[0, disabled ? 16 : 24, 0, icon ? 32 : 16]]
                            : [[0, disabled ? 16 : 38, 0, icon ? 42 : 16]],
                resize: "none",
                textAlign: "left",
                textOverflow: "ellipsis",
                transition: "background-color 200ms, outline-color 200ms",
                WebkitAppearance: "none",
                whiteSpace: "nowrap",
                width: "100%",
            },
            /** Language selector label nudge — aligns text with the globe icon in modern chrome. */
            selectContentGlobe: {
                position: "relative",
                top: 1,
            },
            option: {
                alignItems: "center",
                composes: "select__option",
                cursor: "pointer",
                display: "flex",
                minHeight: theme.OptionHeight,
                outline: "none",
                padding: [[0, 16]],
                transition: "background-color 200ms, color 200ms",
                userSelect: "none",
                color: foregroundColor(theme, theme.ControlBackground),
                "&:hover": {
                    backgroundColor: darken("#fff", theme.ActiveHighlightValue),
                },
                "&:focus": {
                    background: theme.PrimaryColor,
                    color: foregroundColor(theme, theme.PrimaryColor),
                    "&:hover": {
                        backgroundColor: darken(theme.PrimaryColor, theme.ActiveHighlightValue),
                    },
                },
                "&:active": {
                    color: foregroundColor(theme, theme.PrimaryColor),
                    backgroundColor: theme.PrimaryColor,
                },
            },
            optionModern: {
                alignItems: "center",
                backgroundColor: "transparent",
                borderRadius: () => InputTokens.resolveSelectListboxBorderRadius(theme),
                boxSizing: "border-box",
                composes: "select__option",
                cursor: "pointer",
                display: "flex",
                height: InputTokens.SELECT_LISTBOX_OPTION_HEIGHT,
                minHeight: InputTokens.SELECT_LISTBOX_OPTION_HEIGHT,
                outline: "none",
                padding: InputTokens.resolveSelectListboxOptionPadding(),
                transition: "background-color 200ms, color 200ms",
                userSelect: "none",
                color: theme.ForegroundColorDark,
                "&:hover": {
                    backgroundColor: ChoiceTokens.resolveChoiceBackgroundHover(theme),
                },
                "&:focus": {
                    backgroundColor: ChoiceTokens.resolveChoiceBackgroundHover(theme),
                    color: theme.ForegroundColorDark,
                    outline: "none",
                },
                "&:active": {
                    backgroundColor: ChoiceTokens.resolveChoiceBackgroundHover(theme),
                    color: theme.ForegroundColorDark,
                },
            },
            optionSelectedModern: {
                color: theme.PrimaryColor,
                fontWeight: SELECT_OPTION_SELECTED_FONT_WEIGHT,
                "&:hover": {
                    backgroundColor: ChoiceTokens.resolveChoiceBackgroundHover(theme),
                    color: theme.PrimaryColor,
                },
                "&:focus": {
                    backgroundColor: ChoiceTokens.resolveChoiceBackgroundHover(theme),
                    color: theme.PrimaryColor,
                    outline: "none",
                },
                "&:active": {
                    backgroundColor: ChoiceTokens.resolveChoiceBackgroundHover(theme),
                    color: theme.PrimaryColor,
                },
            },
            optionHighlightedModern: {
                backgroundColor: ChoiceTokens.resolveChoiceBackgroundHover(theme),
            },
            optionPlaceholderModern: {
                alignItems: "center",
                backgroundColor: "transparent",
                borderRadius: () => InputTokens.resolveSelectListboxBorderRadius(theme),
                boxSizing: "border-box",
                composes: "select__option",
                cursor: "pointer",
                display: "flex",
                height: InputTokens.SELECT_LISTBOX_OPTION_HEIGHT,
                minHeight: InputTokens.SELECT_LISTBOX_OPTION_HEIGHT,
                outline: "none",
                padding: InputTokens.resolveSelectListboxOptionPadding(),
                transition: "background-color 200ms, color 200ms",
                userSelect: "none",
                color: theme.ForegroundColorDark,
                fontWeight: "normal",
                "&:hover": {
                    backgroundColor: ChoiceTokens.resolveChoiceBackgroundHover(theme),
                    color: theme.ForegroundColorDark,
                },
                "&:focus": {
                    backgroundColor: ChoiceTokens.resolveChoiceBackgroundHover(theme),
                    color: theme.ForegroundColorDark,
                    outline: "none",
                },
                "&:active": {
                    backgroundColor: ChoiceTokens.resolveChoiceBackgroundHover(theme),
                    color: theme.ForegroundColorDark,
                },
            },
            optionNonInteractive: {
                alignItems: "center",
                composes: "select__option",
                cursor: "default",
                display: "flex",
                minHeight: theme.OptionHeight,
                outline: "none",
                padding: [[0, 16]],
                userSelect: "none",
                color: brighten(foregroundColor(theme, controlBackgroundColor(theme)), 2),
            },
            optionHighlighted: {
                backgroundColor: darken("#fff", theme.ActiveHighlightValue),
            },
            autoCompleteOptionHighlighted: {
                backgroundColor: theme.PrimaryColor,
                color: foregroundColor(theme, theme.PrimaryColor),
                "&:hover": {
                    backgroundColor: darken(theme.PrimaryColor, theme.ActiveHighlightValue),
                },
            },
            message: {
                alignItems: "center",
                backgroundColor: theme.ControlBackground,
                composes: "select__message",
                display: "flex",
                fontSize: "0.8em",
                minHeight: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? InputTokens.SELECT_LISTBOX_OPTION_HEIGHT
                        : theme.OptionHeight,
                outline: "none",
                padding: [[0, 16]],
            },
            listbox: {
                background: () =>
                    InputTokens.useModernInputChrome(theme) ? theme.InputBackground : "#FFF",
                listStyleType: "none",
                composes: "select__listbox",
                margin: 0,
                maxHeight: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? InputTokens.SELECT_LISTBOX_OPTION_HEIGHT * 8
                        : theme.OptionHeight * 8,
                outline: "none",
                overflowY: "auto",
                padding: () => (InputTokens.useModernInputChrome(theme) ? [[4, 4]] : 0),
                position: "-webkit-sticky" /* Safari */,
            },
            listboxExpanded: {
                composes: "select__listbox--expanded",
                borderRadius: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? InputTokens.resolveSelectListboxBorderRadius(theme)
                        : undefined,
                borderBottomLeftRadius: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? undefined
                        : Math.min(theme.BorderRadius, theme.InputHeight / 2),
                borderBottomRightRadius: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? undefined
                        : Math.min(theme.BorderRadius, theme.InputHeight / 2),
                overflowY: "auto",
                position: "relative",
            },
            dropDown: {
                boxSizing: "border-box",
                composes: "select__listbox--drop-down",
                position: "sticky",
                marginTop: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? InputTokens.SELECT_LISTBOX_GAP_PX
                        : "auto",
                zIndex: 10,
                border: [[1, "solid", theme.ControlBorderColor]],
            },
            check: {
                composes: "select__check",
                marginLeft: 12,
                transition: "opacity 200ms",
            },
            hidden: {
                composes: "select__listbox--hidden",
                display: "none",
            },
            invisible: {
                opacity: 0,
            },
            icon: {
                composes: "input-box__accessory",
                position: "absolute",
                top: 0,
                bottom: 0,
                fontSize: ({ size }: { size?: string }) => size === "XSmall" && 13,
                margin: "auto",
                pointerEvents: "none",
            },
            iconLeft: {
                color: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? "inherit"
                        : brighten(theme.ForegroundColorDark, 1.6),
                composes: "input-box__accessory-left",
                left: 0,
                marginLeft: ({ size }: { size?: string }) =>
                    InputTokens.useModernInputChrome(theme)
                        ? InputTokens.resolveSelectComboboxAccessoryMargin()
                        : size === "XSmall"
                            ? 8
                            : 16,
            },
            iconCaret: {
                composes: "input-box__accessory-right",
                right: 0,
                marginRight: ({ size }: { size?: string }) =>
                    InputTokens.useModernInputChrome(theme)
                        ? InputTokens.resolveSelectComboboxAccessoryMargin()
                        : size === "XSmall"
                            ? 8
                            : 16,
            },
            iconSvg: {
                color: () =>
                    InputTokens.useModernInputChrome(theme) ? "inherit" : theme.ForegroundColorDark,
                display: "block",
                flexShrink: 0,
            },
            iconSvgGlobe: {
                width: 16,
                height: 16,
            },
            iconSvgCaret: {
                width: 16,
                height: 16,
            },
            /** Legacy combobox focus only — modern focus uses `selectFocused` / `selectExpanded`. */
            choiceActive: {
                composes: "choice--active",
                background: ({ selected }: { selected?: boolean }) => {
                    if (InputTokens.useModernInputChrome(theme)) {
                        return undefined;
                    }
                    if (backgroundless(theme)) {
                        return undefined;
                    }
                    return selected
                        ? darken(theme.PrimaryColor, theme.ActiveHighlightValue)
                        : darken(theme.ControlBackground, theme.ActiveBackgroundValue);
                },
                color: ({ selected }: { selected?: boolean }) => {
                    if (InputTokens.useModernInputChrome(theme)) {
                        return undefined;
                    }
                    return selected
                        ? foregroundColor(
                            theme,
                            darken(theme.PrimaryColor, theme.ActiveHighlightValue),
                        )
                        : foregroundColor(
                            theme,
                            darken(theme.ControlBackground, theme.ActiveBackgroundValue),
                        );
                },
                outlineColor: () =>
                    InputTokens.useModernInputChrome(theme)
                        ? undefined
                        : backgroundless(theme) && theme.PrimaryColor,
            },
        }) as any,
);
